import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Output,
  EventEmitter,
  Renderer2,
  inject,
} from '@angular/core';

type TYPosition = 'start' | 'center-start' | 'center' | 'center-end' | 'end';
type TXPosition = 'start' | 'center' | 'end';

/**
 * Directive that displays any HTMLElement as a static floating tooltip.
 *
 * Attach it to the host element and pass the tooltip element reference via the input.
 * The tooltip element will be positioned using `position: fixed` near the host element.
 * The tooltip remains visible while hovering over the host, tooltip, or the area between them.
 */
@Directive({
  selector: '[libTooltipStatic]',
  standalone: true,
  exportAs: 'tooltipStaticDirective',
})
export class TooltipStaticDirective implements OnDestroy {
  /**
   * Tooltip element that will be shown while the user hovers the host element.
   */
  @Input('libTooltipStatic')
  set tooltip(value: ElementRef<HTMLElement> | HTMLElement | null | undefined) {
    this.tooltipElement = this.resolveElement(value);
    if (this.tooltipElement) {
      this.prepareTooltipElement();
    }
  }

  /** Allows disabling the tooltip behaviour without removing the directive. */
  @Input() tooltipStaticDisabled = false;
  /** Custom z-index applied to the floating tooltip. */
  @Input() tooltipStaticZIndex = 1000;
  /** Horizontal position of the tooltip relative to the host element. */
  @Input() xAxis: TXPosition = 'start';
  /** Vertical position of the tooltip relative to the host element. */
  @Input() yAxis: TYPosition = 'end';
  /** Whether to add distance between tooltip and host element. */
  @Input() hasDistance = true;

  /** Emits true when tooltip is active/visible, false when hidden. */
  @Output() tooltipActiveChange = new EventEmitter<boolean>();

  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);
  private readonly hostElement = inject(ElementRef);

  private tooltipElement: HTMLElement | null = null;
  private tooltipInitialDisplay: string | null = null;
  private readonly windowRef = this.document?.defaultView ?? null;
  private isHoveringTooltip = false;
  private isHoveringHost = false;
  private isAppendedToBody = false;

  @HostListener('mouseenter', ['$event'])
  onMouseEnter(event: MouseEvent): void {
    if (this.tooltipStaticDisabled || !this.tooltipElement || !this.windowRef) {
      return;
    }

    this.isHoveringHost = true;
    this.showTooltip(event);
  }

  @HostListener('mouseleave', ['$event'])
  onMouseLeave(_event: MouseEvent): void {
    this.isHoveringHost = false;

    // Delay para verificar se o mouse entrou no tooltip
    setTimeout(() => {
      if (!this.isHoveringTooltip && !this.isHoveringHost) {
        this.hideTooltip();
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.hideTooltip(true);
    
    // Remove tooltip from body on destroy
    if (this.isAppendedToBody && this.tooltipElement && this.document.body) {
      this.renderer.removeChild(this.document.body, this.tooltipElement);
      this.isAppendedToBody = false;
    }
  }

  /** Ensure tooltip has the required base styles and cached display value. */
  private prepareTooltipElement(): void {
    if (!this.tooltipElement || !this.windowRef) {
      return;
    }

    const computedStyles = this.windowRef.getComputedStyle(this.tooltipElement);
    if (!this.tooltipInitialDisplay) {
      this.tooltipInitialDisplay =
        computedStyles.display !== 'none' ? computedStyles.display : 'block';
    }

    this.renderer.setStyle(this.tooltipElement, 'position', 'fixed');
    this.renderer.setStyle(this.tooltipElement, 'top', '0');
    this.renderer.setStyle(this.tooltipElement, 'left', '0');
    this.renderer.setStyle(this.tooltipElement, 'pointer-events', 'auto');
    this.renderer.setStyle(
      this.tooltipElement,
      'z-index',
      String(this.tooltipStaticZIndex)
    );
    this.renderer.setStyle(this.tooltipElement, 'display', 'none');
    this.renderer.setStyle(this.tooltipElement, 'visibility', 'hidden');
    this.renderer.setStyle(this.tooltipElement, 'opacity', '0');
    this.renderer.setStyle(this.tooltipElement, 'top', '-9999px');
    this.renderer.setStyle(this.tooltipElement, 'left', '-9999px');
  }

  /** Display tooltip and align it with the host element. */
  public showTooltip(_event?: MouseEvent): void {
    if (!this.tooltipElement) {
      return;
    }

    this.prepareTooltipElement();
    
    // Attach listeners before appending to body
    this.attachTooltipListeners();

    // Append tooltip to body if not already done
    if (!this.isAppendedToBody && this.document.body) {
      this.renderer.appendChild(this.document.body, this.tooltipElement);
      this.isAppendedToBody = true;
    }
    
    this.renderer.setStyle(
      this.tooltipElement,
      'display',
      this.tooltipInitialDisplay ?? 'block'
    );
    this.renderer.setStyle(this.tooltipElement, 'visibility', 'visible');
    this.renderer.setStyle(this.tooltipElement, 'opacity', '1');

    // Emit tooltip active state
    this.tooltipActiveChange.emit(true);

    // Aguarda o próximo frame para garantir que o tooltip foi renderizado
    requestAnimationFrame(() => {
      this.updateTooltipPosition();
    });
  }

  /** Hide tooltip and cleanup listeners. */
  public hideTooltip(forceCleanup: boolean = false): void {
    this.detachTooltipListeners();

    if (!this.tooltipElement) {
      return;
    }

    this.renderer.setStyle(this.tooltipElement, 'display', 'none');
    this.renderer.setStyle(this.tooltipElement, 'visibility', 'hidden');
    this.renderer.setStyle(this.tooltipElement, 'opacity', '0');

    // Emit tooltip inactive state
    this.tooltipActiveChange.emit(false);

    if (forceCleanup) {
      this.renderer.removeStyle(this.tooltipElement, 'pointer-events');
      this.renderer.removeStyle(this.tooltipElement, 'top');
      this.renderer.removeStyle(this.tooltipElement, 'left');
      this.renderer.removeStyle(this.tooltipElement, 'position');
      this.renderer.removeStyle(this.tooltipElement, 'z-index');
      this.renderer.removeStyle(this.tooltipElement, 'visibility');
      this.renderer.removeStyle(this.tooltipElement, 'opacity');
      if (this.tooltipInitialDisplay) {
        this.renderer.setStyle(
          this.tooltipElement,
          'display',
          this.tooltipInitialDisplay
        );
      } else {
        this.renderer.removeStyle(this.tooltipElement, 'display');
      }
    }
  }

  /** Update tooltip coordinates based on host element position. */
  private updateTooltipPosition(): void {
    if (!this.tooltipElement || !this.hostElement || !this.windowRef) {
      return;
    }

    const hostRect = this.hostElement.nativeElement.getBoundingClientRect();
    const tooltipRect = this.tooltipElement.getBoundingClientRect();
    const { innerWidth, innerHeight } = this.windowRef;

    let left = this.calcLeftPosition(
      this.xAxis,
      this.yAxis,
      this.tooltipElement
    );
    let top = this.calcTopPosition(this.yAxis, this.tooltipElement);


    // Ajustes para manter o tooltip dentro da viewport
    if (top + tooltipRect.height > innerHeight) {
      top = this.calcTopPosition('start', this.tooltipElement);
    }
    if (top < 0) {
      top = this.calcTopPosition('end', this.tooltipElement);
    }

    if (left + tooltipRect.width > innerWidth && this.xAxis === 'end') {
      left = this.calcLeftPosition('start', this.yAxis, this.tooltipElement);
    }
    if (
      left < 0 ||
      (left + tooltipRect.width > innerWidth && this.xAxis === 'start')
    ) {
      left = this.calcLeftPosition('end', this.yAxis, this.tooltipElement);
    }



       
    this.renderer.setStyle(this.tooltipElement, 'top', `${top}px`);
    this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
    this.renderer.removeStyle(this.tooltipElement, 'transform');
  }

  private calcTopPosition(
    position: TYPosition,
    tooltip: HTMLElement
  ): number {
    const hostRect = this.hostElement.nativeElement.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const distance = this.hasDistance ? 6 : 0;

    let result: number;
    switch (position) {
      case 'start':
        result = hostRect.top - tooltipRect.height - distance;
        break;

      case 'center':
        result = hostRect.top + hostRect.height / 2 - tooltipRect.height / 2;
        break;

      case 'end':
        result = hostRect.top + hostRect.height + distance;
        break;

      default:
        result = hostRect.top - tooltipRect.height - distance;
    }

    return result;
  }

  private calcLeftPosition(
    positionX: TXPosition,
    positionY: TYPosition,
    tooltip: HTMLElement
  ): number {
    const hostRect = this.hostElement.nativeElement.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const distance = this.hasDistance ? 6 : 0;

    let result: number;
    switch (positionX) {
      case 'start':
        result = positionY === 'center'
          ? hostRect.left - tooltipRect.width - distance
          : hostRect.left;
        break;

      case 'center':
        result = hostRect.left + hostRect.width / 2 - tooltipRect.width / 2;
        break;

      case 'end':
        result = positionY === 'center'
          ? hostRect.left + hostRect.width + distance
          : hostRect.left + hostRect.width - tooltipRect.width;
        break;

      default:
        result = positionY === 'center'
          ? hostRect.left - tooltipRect.width - distance
          : hostRect.left;
    }


    return result;
  }

  /** Attach mouse event listeners to the tooltip element. */
  private attachTooltipListeners(): void {
    if (!this.tooltipElement) {
      return;
    }

    this.tooltipElement.addEventListener(
      'mouseenter',
      this.handleTooltipMouseEnter
    );
    this.tooltipElement.addEventListener(
      'mouseleave',
      this.handleTooltipMouseLeave
    );
  }

  /** Detach mouse event listeners from the tooltip element. */
  private detachTooltipListeners(): void {
    if (!this.tooltipElement) {
      return;
    }

    this.tooltipElement.removeEventListener(
      'mouseenter',
      this.handleTooltipMouseEnter
    );
    this.tooltipElement.removeEventListener(
      'mouseleave',
      this.handleTooltipMouseLeave
    );
  }

  private readonly handleTooltipMouseEnter = (): void => {
    this.isHoveringTooltip = true;
  };

  private readonly handleTooltipMouseLeave = (): void => {
    this.isHoveringTooltip = false;

    // Delay para verificar se o mouse voltou para o host
    setTimeout(() => {
      if (!this.isHoveringHost && !this.isHoveringTooltip) {
        this.hideTooltip();
      }
    }, 50);
  };

  /** Check if mouse is in the area between host element and tooltip. */
  private isMouseInTooltipArea(event: MouseEvent): boolean {
    if (!this.tooltipElement || !this.hostElement) {
      return false;
    }

    const hostRect = this.hostElement.nativeElement.getBoundingClientRect();
    const tooltipRect = this.tooltipElement.getBoundingClientRect();

    const mouseX = event.clientX;
    const mouseY = event.clientY;

    // Verifica se o mouse está na área entre o host e o tooltip (considerando o offset)
    const isInHorizontalRange =
      mouseX >= Math.min(hostRect.left, tooltipRect.left) &&
      mouseX <= Math.max(hostRect.right, tooltipRect.right);

    const isInVerticalRange =
      mouseY >= hostRect.bottom && mouseY <= tooltipRect.top;

    return isInHorizontalRange && isInVerticalRange;
  }

  private resolveElement(
    value?: ElementRef<HTMLElement> | HTMLElement | null
  ): HTMLElement | null {
    if (!value) {
      return null;
    }

    if (value instanceof ElementRef) {
      return value.nativeElement;
    }

    return value;
  }
}
